import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';
import useAuth from '@/auth/useAuth';

export function useProductSectionTracking(section: 'gpu_instance' | 'serverless_gpu') {
  const location = useLocation();
  const { session } = useAuth();
  const userId = session?.user?.id;
  const hasTracked = useRef(false);
  const searchDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customModelDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const codeEditDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track section entry
  useEffect(() => {
    if (userId && !hasTracked.current) {
      hasTracked.current = true;
      trackEvent('engagement', 'section_entered', {
        user_id: userId,
        metadata: {
          section,
          entry_path: location.pathname,
        },
      });
    }
  }, [userId, section]);

  // Track clicks on links, buttons, tabs, and interactive elements
  useEffect(() => {
    if (!userId) return;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // PRIORITY 1: Track button clicks first (Run, Deploy, Cancel, etc.)
      const clickedButton = target.closest('button');
      if (clickedButton) {
        const buttonText = clickedButton.textContent?.trim().slice(0, 100) || '';
        const textLower = buttonText.toLowerCase();
        const elementId = clickedButton.id || undefined;

        // Serverless GPU specific buttons
        if (section === 'serverless_gpu') {
          // Run button (exact match or starts with Run)
          if (textLower === 'run' || textLower.startsWith('run')) {
            trackEvent('ui_interaction', 'run_task', {
              user_id: userId,
              element_id: elementId,
              metadata: {
                section,
                action: 'run',
                button_text: buttonText,
                page_path: location.pathname,
              },
            });
            return;
          }

          // Deploy button
          if (textLower === 'deploy' || textLower.startsWith('deploy')) {
            trackEvent('ui_interaction', 'deploy_project', {
              user_id: userId,
              element_id: elementId,
              metadata: {
                section,
                action: 'deploy',
                button_text: buttonText,
                page_path: location.pathname,
              },
            });
            return;
          }

          // Cancel button
          if (textLower === 'cancel' || textLower.startsWith('cancel')) {
            trackEvent('ui_interaction', 'cancel_task', {
              user_id: userId,
              element_id: elementId,
              metadata: {
                section,
                action: 'cancel',
                button_text: buttonText,
                page_path: location.pathname,
              },
            });
            return;
          }

          // Upload button
          if (textLower === 'upload' || textLower.includes('upload')) {
            trackEvent('ui_interaction', 'storage_upload', {
              user_id: userId,
              element_id: elementId,
              metadata: {
                section,
                action: 'upload',
                page_path: location.pathname,
              },
            });
            return;
          }

          // Filter button (tasks)
          if (textLower === 'filter') {
            trackEvent('ui_interaction', 'task_filter_submit', {
              user_id: userId,
              element_id: elementId,
              metadata: {
                section,
                action: 'filter',
                page_path: location.pathname,
              },
            });
            return;
          }

          // Create folder button
          if (textLower.includes('create folder') || textLower.includes('new folder')) {
            trackEvent('ui_interaction', 'storage_create_folder', {
              user_id: userId,
              element_id: elementId,
              metadata: {
                section,
                action: 'create_folder',
                page_path: location.pathname,
              },
            });
            return;
          }
        }

        // Refresh button (check for icon)
        if (clickedButton.querySelector('[class*="RefreshCw"]') || textLower === 'refresh') {
          trackEvent('ui_interaction', 'storage_refresh', {
            user_id: userId,
            element_id: elementId,
            metadata: {
              section,
              action: 'refresh',
              page_path: location.pathname,
            },
          });
          return;
        }

        // Check if it's a tab trigger
        if (clickedButton.getAttribute('role') === 'tab') {
          const tabValue = clickedButton.getAttribute('data-value') ||
            clickedButton.getAttribute('data-state') ||
            buttonText;
          const tabList = clickedButton.closest('[role="tablist"]');

          trackEvent('ui_interaction', 'tab_switch', {
            user_id: userId,
            element_id: elementId,
            metadata: {
              section,
              tab_name: tabValue,
              tab_group: tabList?.getAttribute('aria-label') || 'tabs',
              page_path: location.pathname,
            },
          });
          return;
        }

        // Generic button click (skip if it's just an icon button with no meaningful text)
        if (buttonText && buttonText.length > 0 && !/^[\s×✕]+$/.test(buttonText)) {
          trackEvent('ui_interaction', 'button_click', {
            user_id: userId,
            element_id: elementId,
            metadata: {
              section,
              element_text: buttonText,
              page_path: location.pathname,
            },
          });
        }
        return;
      }

      // PRIORITY 2: Track link clicks (including sidebar navigation)
      const clickedLink = target.closest('a');
      if (clickedLink) {
        const linkText = clickedLink.textContent?.trim().slice(0, 100) || '';
        const href = clickedLink.getAttribute('href') || undefined;

        // Check for explicit nav tracking attribute FIRST
        const navTrackAttr = clickedLink.getAttribute('data-track-nav');
        if (navTrackAttr) {
          trackEvent('ui_interaction', 'sidebar_navigation', {
            user_id: userId,
            metadata: {
              section,
              nav_item: navTrackAttr,
              destination: href,
              page_path: location.pathname,
            },
          });
          return;
        }

        const isNavLink = clickedLink.getAttribute('role') === 'button';

        // Check if this is a sidebar/navigation link
        const isInSidebar = clickedLink.closest('nav') !== null ||
          clickedLink.closest('[class*="sidebar"]') !== null ||
          clickedLink.closest('[class*="navigation"]') !== null ||
          clickedLink.closest('[data-state="expanded"]') !== null ||
          clickedLink.closest('[data-state="collapsed"]') !== null ||
          clickedLink.closest('aside') !== null;

        // Detect navigation items by common labels
        const navLabels = ['overview', 'tasks', 'storage', 'playground', 'deploy', 'develop',
                         'activity', 'setting', 'quickstart', 'projects', 'tokens', 'pricing',
                         'storage logs', 'all projects', 'blueprint'];
        const isNavItem = navLabels.some(label =>
          linkText.toLowerCase().includes(label)
        );

        if (isNavLink || isInSidebar || isNavItem) {
          // This is a sidebar navigation click
          trackEvent('ui_interaction', 'sidebar_navigation', {
            user_id: userId,
            element_id: clickedLink.id || undefined,
            metadata: {
              section,
              nav_item: linkText,
              destination: href,
              page_path: location.pathname,
            },
          });
          return;
        }

        // Regular link click
        trackEvent('ui_interaction', 'link_click', {
          user_id: userId,
          element_id: clickedLink.id || undefined,
          metadata: {
            section,
            element_text: linkText,
            href,
            page_path: location.pathname,
          },
        });
        return;
      }

      // PRIORITY 3: Track dropdown menu items (Storage actions)
      const dropdownItem = target.closest('[role="menuitem"]');
      if (dropdownItem) {
        const itemText = dropdownItem.textContent?.trim();

        if (itemText) {
          const actionMap: Record<string, string> = {
            'Preview': 'storage_preview',
            'Download': 'storage_download',
            'Copy to': 'storage_copy',
            'Delete': 'storage_delete',
          };

          const action = actionMap[itemText];
          if (action) {
            trackEvent('ui_interaction', action, {
              user_id: userId,
              metadata: {
                section,
                action_type: itemText.toLowerCase(),
                page_path: location.pathname,
              },
            });
            return;
          }
        }
      }

      // PRIORITY 4: Track model card selection (GPU Instance - not a button)
      const modelCard = target.closest('[class*="rounded-lg"][class*="border"]:not(button)');
      if (modelCard && modelCard.tagName.toLowerCase() !== 'button') {
        const modelName = modelCard.querySelector('.font-medium')?.textContent?.trim();
        const modelVram = modelCard.textContent?.match(/(\d+)GB/)?.[1];
        const hasCheckbox = modelCard.querySelector('[class*="rounded"][class*="border-2"]');

        if (modelName && hasCheckbox) {
          const isSelected = modelCard.classList.contains('border-primary') ||
            modelCard.querySelector('[class*="bg-primary"]') !== null;

          trackEvent('ui_interaction', 'model_select', {
            user_id: userId,
            metadata: {
              section,
              model_name: modelName.slice(0, 100),
              model_vram: modelVram ? `${modelVram}GB` : undefined,
              action: isSelected ? 'deselect' : 'select',
              model_type: 'preset',
              page_path: location.pathname,
            },
          });
          return;
        }
      }

      // PRIORITY 5: Track ability toggle clicks (custom model configuration)
      const abilityToggle = target.closest('[class*="rounded-md"][class*="border"][class*="cursor-pointer"]:not(button)');
      if (abilityToggle && abilityToggle.tagName.toLowerCase() !== 'button') {
        const abilityText = abilityToggle.querySelector('.capitalize')?.textContent?.trim();
        if (abilityText) {
          const isSelected = abilityToggle.classList.contains('border-primary') ||
            abilityToggle.querySelector('[class*="bg-primary"]') !== null;

          trackEvent('ui_interaction', 'ability_toggle', {
            user_id: userId,
            metadata: {
              section,
              ability_name: abilityText,
              action: isSelected ? 'disable' : 'enable',
              page_path: location.pathname,
            },
          });
          return;
        }
      }

      // PRIORITY 6: Track category filter badge clicks
      const filterBadge = target.closest('[class*="cursor-pointer"][class*="transition-colors"]:not(button)');
      if (filterBadge && filterBadge.tagName.toLowerCase() !== 'button') {
        const badgeText = filterBadge.textContent?.trim();
        if (badgeText && /^[A-Za-z]+\s*\(\d+\)$/.test(badgeText)) {
          trackEvent('ui_interaction', 'filter_select', {
            user_id: userId,
            metadata: {
              section,
              filter_name: 'category',
              selected_value: badgeText.replace(/\s*\(\d+\)$/, ''),
              page_path: location.pathname,
            },
          });
          return;
        }
      }

      // PRIORITY 7: Track breadcrumb navigation
      const breadcrumbLink = target.closest('[class*="BreadcrumbLink"], [class*="breadcrumb"] span[class*="cursor"]');
      if (breadcrumbLink) {
        const folderName = breadcrumbLink.textContent?.trim();
        trackEvent('ui_interaction', 'storage_navigate', {
          user_id: userId,
          metadata: {
            section,
            folder_name: folderName,
            page_path: location.pathname,
          },
        });
        return;
      }

      // PRIORITY 8: Track table row clicks (Storage file/folder selection)
      const tableRow = target.closest('tr');
      if (tableRow && tableRow.classList.contains('cursor-pointer')) {
        const fileName = tableRow.querySelector('td:first-child')?.textContent?.trim();
        const fileType = tableRow.textContent?.includes('directory') ? 'folder' : 'file';

        if (fileName) {
          trackEvent('ui_interaction', 'storage_item_click', {
            user_id: userId,
            metadata: {
              section,
              item_name: fileName.slice(0, 100),
              item_type: fileType,
              page_path: location.pathname,
            },
          });
          return;
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [userId, section, location.pathname]);

  // Track search input, custom model input, and code editing (debounced)
  useEffect(() => {
    if (!userId) return;

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      const tagName = target.tagName.toLowerCase();

      if (tagName !== 'input' && tagName !== 'textarea') return;

      const inputValue = target.value.trim();

      // Check if it's a search input (Task ID search)
      const isSearchInput =
        target.type === 'search' ||
        target.name?.toLowerCase().includes('search') ||
        target.placeholder?.toLowerCase().includes('search') ||
        target.placeholder?.toLowerCase().includes('task id') ||
        target.id?.toLowerCase().includes('search') ||
        target.closest('[data-search]');

      if (isSearchInput) {
        if (searchDebounceTimer.current) {
          clearTimeout(searchDebounceTimer.current);
        }

        searchDebounceTimer.current = setTimeout(() => {
          if (inputValue.length > 0) {
            trackEvent('ui_interaction', 'search', {
              user_id: userId,
              element_id: target.id || undefined,
              metadata: {
                section,
                search_query: inputValue.slice(0, 200),
                search_type: target.placeholder?.toLowerCase().includes('task') ? 'task_id' : 'general',
                page_path: location.pathname,
              },
            });
          }
        }, 500);
        return;
      }

      // Check if it's a custom model input (HuggingFace model ID) - GPU Instance
      const isCustomModelInput =
        target.placeholder?.toLowerCase().includes('llama') ||
        target.placeholder?.toLowerCase().includes('huggingface') ||
        target.placeholder?.includes('/') ||
        target.closest('[class*="HuggingFace"]');

      if (isCustomModelInput) {
        if (customModelDebounceTimer.current) {
          clearTimeout(customModelDebounceTimer.current);
        }

        customModelDebounceTimer.current = setTimeout(() => {
          if (inputValue.length > 0) {
            trackEvent('ui_interaction', 'custom_model_input', {
              user_id: userId,
              element_id: target.id || undefined,
              metadata: {
                section,
                model_id: inputValue.slice(0, 200),
                page_path: location.pathname,
              },
            });
          }
        }, 800);
        return;
      }

      // Check if it's a file name input (Serverless GPU playground)
      const isFileNameInput =
        target.closest('[class*="items-center"]')?.textContent?.includes('.py');

      if (isFileNameInput && section === 'serverless_gpu') {
        trackEvent('ui_interaction', 'file_name_change', {
          user_id: userId,
          element_id: target.id || undefined,
          metadata: {
            section,
            file_name: inputValue.slice(0, 100),
            page_path: location.pathname,
          },
        });
      }
    };

    // Track paste events
    const handlePaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLInputElement;
      if (target.tagName.toLowerCase() !== 'input') return;

      const pastedText = event.clipboardData?.getData('text')?.trim();
      if (!pastedText) return;

      // HuggingFace model ID paste
      const isModelId = pastedText.includes('/') && !pastedText.includes(' ');
      if (isModelId) {
        trackEvent('ui_interaction', 'custom_model_paste', {
          user_id: userId,
          element_id: target.id || undefined,
          metadata: {
            section,
            model_id: pastedText.slice(0, 200),
            page_path: location.pathname,
          },
        });
      }
    };

    document.addEventListener('input', handleInput, true);
    document.addEventListener('paste', handlePaste, true);

    return () => {
      document.removeEventListener('input', handleInput, true);
      document.removeEventListener('paste', handlePaste, true);
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
      if (customModelDebounceTimer.current) {
        clearTimeout(customModelDebounceTimer.current);
      }
    };
  }, [userId, section, location.pathname]);

  // Track Monaco Editor code changes (Serverless GPU)
  useEffect(() => {
    if (!userId || section !== 'serverless_gpu') return;

    let lastCodeLength = 0;
    let observer: MutationObserver | null = null;
    let checkInterval: ReturnType<typeof setInterval> | null = null;
    let isTracking = false;

    const trackCodeChange = (currentLength: number) => {
      // Only track significant changes (more than 5 character difference)
      if (Math.abs(currentLength - lastCodeLength) > 5) {
        if (codeEditDebounceTimer.current) {
          clearTimeout(codeEditDebounceTimer.current);
        }

        codeEditDebounceTimer.current = setTimeout(() => {
          trackEvent('ui_interaction', 'code_edit', {
            user_id: userId,
            metadata: {
              section,
              code_length: currentLength,
              change_size: currentLength - lastCodeLength,
              page_path: location.pathname,
            },
          });
          lastCodeLength = currentLength;
        }, 1500);
      }
    };

    // Handle keyboard input in Monaco editor
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      // Check if the event is from Monaco editor's textarea
      if (target.classList.contains('monaco-mouse-cursor-text') ||
          target.closest('.monaco-editor') ||
          target.classList.contains('inputarea')) {

        // Ignore navigation keys
        const ignoredKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
                           'Home', 'End', 'PageUp', 'PageDown', 'Escape',
                           'Control', 'Alt', 'Shift', 'Meta'];
        if (ignoredKeys.includes(event.key)) return;

        // Get current content length
        setTimeout(() => {
          const monacoContainer = document.querySelector('.monaco-editor');
          if (monacoContainer) {
            const viewLines = monacoContainer.querySelector('.view-lines');
            if (viewLines) {
              const currentLength = viewLines.textContent?.length || 0;
              trackCodeChange(currentLength);
            }
          }
        }, 100);
      }
    };

    // Handle paste in Monaco editor
    const handlePaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement;

      if (target.closest('.monaco-editor') || target.classList.contains('inputarea')) {
        setTimeout(() => {
          const monacoContainer = document.querySelector('.monaco-editor');
          if (monacoContainer) {
            const viewLines = monacoContainer.querySelector('.view-lines');
            if (viewLines) {
              const currentLength = viewLines.textContent?.length || 0;
              trackCodeChange(currentLength);
            }
          }
        }, 100);
      }
    };

    // Setup observer when Monaco is available
    const setupTracking = () => {
      const monacoContainer = document.querySelector('.monaco-editor');
      if (monacoContainer && !isTracking) {
        isTracking = true;

        // Get initial code length
        const viewLines = monacoContainer.querySelector('.view-lines');
        if (viewLines) {
          lastCodeLength = viewLines.textContent?.length || 0;
        }

        // Track initial editor focus
        trackEvent('ui_interaction', 'code_editor_opened', {
          user_id: userId,
          metadata: {
            section,
            initial_code_length: lastCodeLength,
            page_path: location.pathname,
          },
        });

        // Setup MutationObserver for DOM changes
        observer = new MutationObserver(() => {
          const viewLines = monacoContainer.querySelector('.view-lines');
          if (viewLines) {
            const currentLength = viewLines.textContent?.length || 0;
            trackCodeChange(currentLength);
          }
        });

        observer.observe(monacoContainer, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        // Clear the check interval once we're tracking
        if (checkInterval) {
          clearInterval(checkInterval);
          checkInterval = null;
        }
      }
    };

    // Check for Monaco editor periodically until found
    checkInterval = setInterval(setupTracking, 500);

    // Also try immediately
    setupTracking();

    // Add keyboard and paste listeners
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('paste', handlePaste, true);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      if (codeEditDebounceTimer.current) {
        clearTimeout(codeEditDebounceTimer.current);
      }
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('paste', handlePaste, true);
    };
  }, [userId, section, location.pathname]);

  // Track filter/select changes
  useEffect(() => {
    if (!userId) return;

    const handleSelectChange = (event: Event) => {
      const target = event.target as HTMLSelectElement | HTMLInputElement;

      // Handle native <select> elements
      if (target.tagName.toLowerCase() === 'select') {
        const selectElement = target as HTMLSelectElement;
        const selectedOption = selectElement.options[selectElement.selectedIndex];

        const label = selectElement.closest('.space-y-2')?.querySelector('label')?.textContent?.trim();
        const filterName = label || selectElement.name || selectElement.id || 'unknown';

        trackEvent('ui_interaction', 'filter_select', {
          user_id: userId,
          element_id: selectElement.id || selectElement.name || undefined,
          metadata: {
            section,
            filter_name: filterName,
            selected_value: selectElement.value,
            selected_text: selectedOption?.text || '',
            page_path: location.pathname,
          },
        });
        return;
      }

      // Handle radio buttons and checkboxes
      if (target.tagName.toLowerCase() === 'input') {
        const inputElement = target as HTMLInputElement;

        if (inputElement.type === 'radio' || inputElement.type === 'checkbox') {
          const isFilter =
            inputElement.name?.toLowerCase().includes('filter') ||
            inputElement.closest('[data-filter]') ||
            inputElement.closest('[role="listbox"]') ||
            inputElement.closest('[role="combobox"]');

          if (isFilter) {
            trackEvent('ui_interaction', 'filter_select', {
              user_id: userId,
              element_id: inputElement.id || inputElement.name || undefined,
              metadata: {
                section,
                filter_name: inputElement.name || 'unknown',
                selected_value: inputElement.value,
                checked: inputElement.checked,
                page_path: location.pathname,
              },
            });
          }
        }
      }
    };

    // Track Radix Select and MultiSelect changes
    const handleCustomSelectClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Radix Select option
      const selectOption = target.closest(
        '[role="option"], [data-radix-collection-item], [cmdk-item], [data-filter-option]'
      );

      if (selectOption) {
        const listbox = selectOption.closest('[role="listbox"], [data-radix-select-viewport], [cmdk-list]');
        const optionText = selectOption.textContent?.trim().slice(0, 100) || '';
        const optionValue =
          selectOption.getAttribute('data-value') ||
          selectOption.getAttribute('value') ||
          optionText;

        // Determine filter type based on context
        let filterType = 'custom_select';
        if (location.pathname.includes('task')) {
          if (optionText.match(/running|completed|failed|pending|cancelled/i)) {
            filterType = 'task_status';
          } else if (optionText.match(/manual|spot|realtime|scheduled/i)) {
            filterType = 'task_type';
          }
        }

        trackEvent('ui_interaction', 'filter_select', {
          user_id: userId,
          element_id: listbox?.id || selectOption.id || undefined,
          metadata: {
            section,
            filter_name: filterType,
            selected_value: optionValue,
            selected_text: optionText,
            page_path: location.pathname,
          },
        });
        return;
      }

      // MultiSelect badge removal (deselecting a filter option)
      const badgeClose = target.closest('[class*="Badge"] button, [class*="badge"] button');
      if (badgeClose) {
        const badge = badgeClose.closest('[class*="Badge"], [class*="badge"]');
        const badgeText = badge?.textContent?.replace('×', '').trim();

        if (badgeText) {
          trackEvent('ui_interaction', 'filter_deselect', {
            user_id: userId,
            metadata: {
              section,
              deselected_value: badgeText,
              page_path: location.pathname,
            },
          });
        }
      }
    };

    // Track date picker changes
    const handleDatePickerClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Date picker day selection
      const dayButton = target.closest('[name="day"]');
      if (dayButton) {
        const dateText = dayButton.getAttribute('aria-label') ||
          dayButton.textContent?.trim();

        trackEvent('ui_interaction', 'date_filter_select', {
          user_id: userId,
          metadata: {
            section,
            selected_date: dateText,
            page_path: location.pathname,
          },
        });
      }
    };

    document.addEventListener('change', handleSelectChange, true);
    document.addEventListener('click', handleCustomSelectClick, true);
    document.addEventListener('click', handleDatePickerClick, true);

    return () => {
      document.removeEventListener('change', handleSelectChange, true);
      document.removeEventListener('click', handleCustomSelectClick, true);
      document.removeEventListener('click', handleDatePickerClick, true);
    };
  }, [userId, section, location.pathname]);
}
